import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Building2, UserPlus, LogIn, KeyRound, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PartnerLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get('mode') === 'register' ? 'signup' : 'login');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', password: '', company: '',
    resetCode: '', newPassword: '', confirmPassword: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'login') {
        const res = await axios.post(`${API}/api/partner-portal/login`, { email: formData.email, password: formData.password });
        localStorage.setItem('partner_token', res.data.access_token);
        localStorage.setItem('partner_info', JSON.stringify(res.data.partner));
        toast.success('Login successful!');
        navigate('/partner/dashboard');
      } else if (mode === 'signup') {
        if (!formData.name) { toast.error('Please fill in all required fields'); setLoading(false); return; }
        const res = await axios.post(`${API}/api/partner-portal/signup`, {
          name: formData.name, email: formData.email, phone: formData.phone, password: formData.password, company: formData.company
        });
        localStorage.setItem('partner_token', res.data.access_token);
        localStorage.setItem('partner_info', JSON.stringify(res.data.partner));
        toast.success('Account created!');
        navigate('/partner/dashboard');
      } else if (mode === 'forgot') {
        await axios.post(`${API}/api/partner-portal/forgot-password`, { email: formData.email });
        toast.success('If an account exists with this email, a reset code has been sent.');
        setMode('reset');
      } else if (mode === 'reset') {
        if (formData.newPassword !== formData.confirmPassword) {
          toast.error('Passwords do not match');
          setLoading(false);
          return;
        }
        if (formData.newPassword.length < 6) {
          toast.error('Password must be at least 6 characters');
          setLoading(false);
          return;
        }
        await axios.post(`${API}/api/partner-portal/reset-password`, {
          email: formData.email,
          reset_code: formData.resetCode,
          new_password: formData.newPassword
        });
        toast.success('Password reset successfully! You can now login.');
        setMode('login');
        setFormData({ ...formData, password: '', resetCode: '', newPassword: '', confirmPassword: '' });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred');
    } finally { setLoading(false); }
  };

  const getTitle = () => {
    switch (mode) {
      case 'signup': return 'Create Partner Account';
      case 'forgot': return 'Forgot Password';
      case 'reset': return 'Reset Password';
      default: return 'Partner Login';
    }
  };

  const getDescription = () => {
    switch (mode) {
      case 'signup': return 'Register to view and manage client referrals sent to you';
      case 'forgot': return "Enter your email and we'll send you a reset code";
      case 'reset': return 'Enter the code sent to your email and create a new password';
      default: return 'Sign in to view referrals and update their status';
    }
  };

  const getIcon = () => {
    switch (mode) {
      case 'signup': return <UserPlus className="w-5 h-5" />;
      case 'forgot':
      case 'reset': return <KeyRound className="w-5 h-5" />;
      default: return <LogIn className="w-5 h-5" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-500/20 mb-4">
            <Building2 className="w-8 h-8 text-blue-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Utmost Financial Inc.</h1>
          <p className="text-slate-400">Business Partner Portal</p>
        </div>

        <Card className="border-slate-700 bg-slate-800/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              {getIcon()}
              {getTitle()}
            </CardTitle>
            <CardDescription className="text-slate-400">
              {getDescription()}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Signup Fields */}
              {mode === 'signup' && (
                <>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Business Name *</Label>
                    <Input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="Your business name" className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500" required data-testid="partner-signup-name" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Company</Label>
                    <Input value={formData.company} onChange={e => setFormData({ ...formData, company: e.target.value })} placeholder="Company name (optional)" className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Phone</Label>
                    <Input value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} placeholder="(204) 555-0123" className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500" data-testid="partner-signup-phone" />
                  </div>
                </>
              )}

              {/* Email - shown for login, signup, forgot */}
              {(mode === 'login' || mode === 'signup' || mode === 'forgot') && (
                <div className="space-y-2">
                  <Label className="text-slate-300">Email *</Label>
                  <Input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} placeholder="you@example.com" className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500" required data-testid="partner-email-input" />
                </div>
              )}

              {/* Password - shown for login, signup */}
              {(mode === 'login' || mode === 'signup') && (
                <div className="space-y-2">
                  <Label className="text-slate-300">Password *</Label>
                  <Input type="password" value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} placeholder="••••••••" className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500" required data-testid="partner-password-input" />
                </div>
              )}

              {/* Reset Code and New Password */}
              {mode === 'reset' && (
                <>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Reset Code</Label>
                    <Input
                      type="text"
                      value={formData.resetCode}
                      onChange={e => setFormData({ ...formData, resetCode: e.target.value })}
                      placeholder="Enter 6-digit code"
                      className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 text-center text-xl tracking-widest"
                      maxLength={6}
                      required
                      data-testid="partner-reset-code-input"
                    />
                    <p className="text-xs text-slate-500">Check your email for the code</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">New Password</Label>
                    <Input
                      type="password"
                      value={formData.newPassword}
                      onChange={e => setFormData({ ...formData, newPassword: e.target.value })}
                      placeholder="••••••••"
                      className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                      required
                      data-testid="partner-new-password-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Confirm Password</Label>
                    <Input
                      type="password"
                      value={formData.confirmPassword}
                      onChange={e => setFormData({ ...formData, confirmPassword: e.target.value })}
                      placeholder="••••••••"
                      className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                      required
                      data-testid="partner-confirm-password-input"
                    />
                  </div>
                </>
              )}

              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading} data-testid="partner-submit-btn">
                {loading ? 'Please wait...' : (
                  mode === 'login' ? 'Sign In' :
                  mode === 'signup' ? 'Create Account' :
                  mode === 'forgot' ? 'Send Reset Code' :
                  'Reset Password'
                )}
              </Button>
            </form>

            {/* Forgot Password Link - shown on login */}
            {mode === 'login' && (
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => setMode('forgot')}
                  className="text-slate-400 hover:text-blue-400 text-sm"
                  data-testid="partner-forgot-password-link"
                >
                  Forgot your password?
                </button>
              </div>
            )}

            {/* Toggle between Login/Signup */}
            {(mode === 'login' || mode === 'signup') && (
              <div className="mt-4 text-center">
                <button type="button" onClick={() => setMode(mode === 'login' ? 'signup' : 'login')} className="text-blue-400 hover:text-blue-300 text-sm" data-testid="partner-toggle-mode">
                  {mode === 'login' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
                </button>
              </div>
            )}

            {/* Back to Login - shown on forgot/reset */}
            {(mode === 'forgot' || mode === 'reset') && (
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => {
                    setMode('login');
                    setFormData({ ...formData, resetCode: '', newPassword: '', confirmPassword: '' });
                  }}
                  className="text-blue-400 hover:text-blue-300 text-sm inline-flex items-center gap-1"
                  data-testid="partner-back-to-login-link"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Login
                </button>
              </div>
            )}

            <div className="mt-4 pt-4 border-t border-slate-700 text-center">
              <Link to="/login" className="text-slate-400 hover:text-slate-300 text-sm flex items-center justify-center gap-1">
                <ArrowLeft className="w-4 h-4" /> Back to Admin Login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
